import { LEGAL_DOCUMENT_SLUGS, type LegalDocumentSlug } from "@pufimi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { LegalEditor } from "@/components/legal-documents/legal-editor";
import { adminLegalDocumentQueryKey, adminLegalDocumentsQueryKey } from "@/lib/admin-queries";
import { adminApi } from "@/lib/api";
import { formatDate } from "@/lib/format";

function isKnownSlug(slug: string): slug is LegalDocumentSlug {
  return (LEGAL_DOCUMENT_SLUGS as readonly string[]).includes(slug);
}

// The markdown editor for one legal document (#305). Owns the query + the two
// append-only mutations (save-draft / publish); the presentational `LegalEditor`
// owns the buffer + actions. Both mutations invalidate this document's cache AND
// the section list, so the header status + the list badge re-read the persisted
// state with no manual refetch.
function LegalDocumentEditorRoute() {
  const { slug } = Route.useParams();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: adminLegalDocumentQueryKey(slug),
    queryFn: () => adminApi.legalDocuments.get(slug),
    enabled: isKnownSlug(slug),
  });

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: adminLegalDocumentQueryKey(slug) }),
      queryClient.invalidateQueries({ queryKey: adminLegalDocumentsQueryKey() }),
    ]);

  const saveDraft = useMutation({
    mutationFn: (contentMd: string) => adminApi.legalDocuments.saveDraft(slug, contentMd),
    onSuccess: invalidate,
  });
  const publish = useMutation({
    mutationFn: (contentMd: string) => adminApi.legalDocuments.publish(slug, contentMd),
    onSuccess: invalidate,
  });

  const backLink = (
    <Link
      to="/legal-documents"
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft className="size-4" aria-hidden />
      Dokumenty prawne
    </Link>
  );

  if (!isKnownSlug(slug)) {
    return (
      <div className="space-y-6">
        {backLink}
        <p className="text-sm text-destructive">Nieznany dokument.</p>
      </div>
    );
  }

  const doc = query.data;

  return (
    <div className="space-y-6">
      {backLink}

      <header className="space-y-1">
        <h1 className="font-display text-2xl font-semibold tracking-tight">
          {doc?.title ?? "Dokument prawny"}
        </h1>
        <p className="text-sm text-muted-foreground">
          {doc
            ? doc.latestVersion !== null && doc.publishedAt !== null
              ? `Opublikowana wersja ${doc.latestVersion} z dnia ${formatDate(doc.publishedAt)}${
                  doc.hasDraft ? " · masz zapisany szkic z niepublikowanymi zmianami" : ""
                }`
              : doc.hasDraft
                ? "Jeszcze nieopublikowany · masz zapisany szkic"
                : "Jeszcze nieopublikowany"
            : " "}
        </p>
      </header>

      {query.isPending ? (
        <p className="text-sm text-muted-foreground">Ładowanie…</p>
      ) : query.isError || !doc ? (
        <p className="text-sm text-destructive">Nie udało się wczytać dokumentu.</p>
      ) : (
        <LegalEditor
          key={doc.slug}
          document={doc}
          onSaveDraft={(contentMd) => saveDraft.mutateAsync(contentMd).then(() => undefined)}
          onPublish={(contentMd) => publish.mutateAsync(contentMd).then(() => undefined)}
        />
      )}
    </div>
  );
}

export const Route = createFileRoute("/legal-documents/$slug")({
  component: LegalDocumentEditorRoute,
});
